import React, {
	memo,
	useCallback,
	useState,
	type MouseEvent,
	type ChangeEvent,
	type KeyboardEvent,
	useMemo,
	useImperativeHandle,
} from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import CalendarIcon from '@atlaskit/icon/core/migration/calendar';
import { createLocalizationProvider } from '@atlaskit/locale';
import Popup, { type TriggerProps } from '@atlaskit/popup'; // ignore-for-ENGHEALTH-17759
import { Flex, Text, xcss } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { FixItErrorFlag } from '@atlassian/jira-business-fields/src/common/ui/fixit-error-flag/index.tsx';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useEditField } from '@atlassian/jira-issue-field-base/src/services/edit-field-service/main.tsx';
import { standardizeLocale } from '@atlassian/jira-issue-format-date/src/common/utils.tsx';
import { getLocale } from '@atlassian/jira-platform-utils-date-fns/src/main.tsx';
import { DateOnly } from '@atlassian/jira-platform-utils-date-only/src/index.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ColoredDueDate } from '@atlassian/jira-coloured-due-date/src/ui/index.tsx';
import { DATE_LOZENGE_ID } from '../../../common/constants.tsx';
import { EmptyCellContent, SHADOW_SIZE } from '../../../common/ui/list-cell/styled.tsx';
import Calendar from './async-calendar/index.tsx';
import type { SelectEvent } from './async-calendar/types.tsx';
import messages from './messages.tsx';
import type { DateCellProps } from './types.tsx';

const DATE_FORMAT_OPTIONS = {
	year: 'numeric',
	month: 'short',
	day: 'numeric',
} as const;

const ALL_NUMERIC_DATE_FORMAT_OPTIONS = {
	year: 'numeric',
	month: 'numeric',
	day: 'numeric',
	format: 'dd/mm/yyyy',
} as const;

const NUMERIC_DATE_FORMAT_REGEX = /^[0-9]{1,2}[-/][0-9]{1,2}[-/][0-9]{4}$/;

const INVALID_DATE = 'Invalid Date';
const EMPTY_ARR: Array<string> = [];
const TEST_ID_PREFIX = 'business-list.ui.list-view.date-cell.date-cell';

const CALENDAR_PROPS = {
	style: {
		display: 'inline-block',
		boxShadow: token('elevation.shadow.raised'),
	},
};

const POPUP_OFFSET: [number, number] = [0, 2 * gridSize];

const DateCell = memo<DateCellProps>(
	({
		dateOnly,
		readOnly = false,
		issueKey = '',
		isDue = false,
		onOpenIssueModal,
		rowData,
		fieldKey,
		fieldValue = null,
		fieldType = '',
		wrapperRef,
		testId,
		editMode = false,
		setEditMode = noop,
		isHovering = false,
		cellRef,
		fieldName = '',
		issueType = '',
		issueTypeId = '',
		inputValue,
		onInputChange = noop,
		issueHasUnreadComment,
	}: DateCellProps) => {
		const locale = useLocale();
		const weekStartDay = getLocale(locale)?.options?.weekStartsOn;
		const standardizedLocale = standardizeLocale(locale);
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { formatDate: formatDateIntl, formatMessage } = useIntl();
		const formatDate = useCallback(
			(
				date: DateOnly | null,
				dateFormatOptions: Intl.DateTimeFormatOptions = DATE_FORMAT_OPTIONS,
			) =>
				date
					? formatDateIntl(
							date.toLocalDate(),
							expVal('color_due_date_all_views', 'showColoredDueDate', false)
								? dateFormatOptions
								: DATE_FORMAT_OPTIONS,
						)
					: '',
			[formatDateIntl],
		);

		const [localizationProvider] = useState(createLocalizationProvider(standardizedLocale));

		const [{ value: ISOStringValue_, error }, { saveValue: saveISOStringValue, resetError }] =
			useEditField<string | null, null>({
				fieldKey,
				issueKey,
				fieldType,
				initialValue: dateOnly?.toISOString() || fieldValue,
			});

		const ISOStringValue = useMemo(() => {
			/**
			 * dateTime -> date conversion
			 * This prevents DateOnly.fromISODateString(ISOStringValue) from throwing when copy-pasting values from "Created" to "Due Date"
			 */
			if (ISOStringValue_ && !DateOnly.isISODateString(ISOStringValue_)) {
				return DateOnly.fromDateAccordingToLocalTime(new Date(ISOStringValue_)).toISOString();
			}
			return ISOStringValue_;
		}, [ISOStringValue_]);

		const currentValue = useMemo(
			() => formatDate(ISOStringValue !== null ? DateOnly.fromISODateString(ISOStringValue) : null),
			[ISOStringValue, formatDate],
		);
		const [textInputValue, setTextInputValue] = [inputValue ?? currentValue, onInputChange];

		const formatDateString = useCallback(
			(dateString: string | null) =>
				dateString === null || dateString === ''
					? ''
					: formatDate(DateOnly.fromISODateString(dateString)),
			[formatDate],
		);

		const saveAndClose = useCallback(() => {
			setEditMode(false);
			if (textInputValue === formatDateString(ISOStringValue)) {
				return;
			}
			// In order to support entering dates with the format '16 Jun 2022',
			// we first attempt to convert the input value into 'dd/mm/yyyy'.
			// If the format is different, an 'Invalid Date' is returned instead
			// Visit https://formatjs.io/docs/intl/#formatdate for more information
			const formattedInputValue = formatDateIntl(
				new Date(String(textInputValue)),
				ALL_NUMERIC_DATE_FORMAT_OPTIONS,
			);

			const isFormattedInputValid =
				// formattedInputValue should have the format 'dd/mm/yyyy' which is checked by regex, the same check is implemented in DateOnly.fromISODateString
				// INVALID_DATE doesn't cover the cases like year 80000, month 01, day 01 which is considered invalid, regex condition checks it!
				formattedInputValue !== INVALID_DATE && NUMERIC_DATE_FORMAT_REGEX.test(formattedInputValue);

			const parsedDate = localizationProvider.parseDate(
				isFormattedInputValid ? formattedInputValue : String(textInputValue),
			);
			const isEmptyValue = textInputValue === '';
			const isNewDateInputValid = !Number.isNaN(Number(parsedDate)) || isEmptyValue;
			if (isNewDateInputValid) {
				saveISOStringValue(
					isEmptyValue
						? null
						: DateOnly.fromDateAccordingToLocalTime(new Date(parsedDate)).toISOString(),
					null,
					createAnalyticsEvent({}),
				);
			} else {
				setTextInputValue(formatDateString(ISOStringValue));
			}
		}, [
			setEditMode,
			textInputValue,
			formatDateString,
			ISOStringValue,
			formatDateIntl,
			localizationProvider,
			saveISOStringValue,
			createAnalyticsEvent,
			setTextInputValue,
		]);

		const handleClick = useCallback(
			(event: MouseEvent) => {
				if (!readOnly && !editMode) {
					setEditMode(true);
				}

				if (onOpenIssueModal && rowData) {
					onOpenIssueModal({ id: rowData.id, key: rowData.key })(event);

					fg('_jira_unread_comments_in_list_view_gate') &&
						fireUIAnalytics(createAnalyticsEvent({}), 'dateCell clicked', {
							issueHasUnreadComment,
						});
				}

				return true;
			},
			[
				readOnly,
				editMode,
				onOpenIssueModal,
				rowData,
				setEditMode,
				createAnalyticsEvent,
				issueHasUnreadComment,
			],
		);

		const handleDateSelect = useCallback(
			({ iso }: SelectEvent) => {
				setTextInputValue(formatDate(DateOnly.fromISODateString(iso)));
				saveISOStringValue(iso, null, createAnalyticsEvent({}));
				setEditMode(false);
				// setTimeout used here as there is conflict with textfield autofocus
				setTimeout(() => {
					wrapperRef?.current?.focus();
				});
			},
			[
				setTextInputValue,
				formatDate,
				saveISOStringValue,
				createAnalyticsEvent,
				setEditMode,
				wrapperRef,
			],
		);

		const handleChange = useCallback(
			(e: ChangeEvent<HTMLInputElement>) => {
				resetError();
				setTextInputValue(e.target.value);
			},
			[resetError, setTextInputValue],
		);

		const handleKeyDownNew = useCallback(
			(e: KeyboardEvent<HTMLElement>) => {
				if (
					readOnly &&
					!['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.code)
				) {
					setEditMode(false);
					return true;
				}

				if (editMode) {
					if (!(e.target instanceof HTMLElement)) {
						return;
					}

					// allow keyboard navigation in the datepicker popup component
					if (['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.code)) {
						return true;
					}
					if (
						['Enter', 'Space'].includes(e.code) &&
						[`${TEST_ID_PREFIX}--previous-month`, `${TEST_ID_PREFIX}--next-month`].includes(
							e.target.getAttribute('data-testid') ?? '',
						)
					) {
						return true;
					}

					// save value on hitting enter in text input
					if (e.key === 'Enter' && e.target.nodeName === 'INPUT') {
						saveAndClose();
						return false;
					}
				}
			},
			[editMode, readOnly, saveAndClose, setEditMode],
		);

		const defaultDate =
			ISOStringValue === null || ISOStringValue === ''
				? DateOnly.fromDateAccordingToLocalTime(new Date()).toISOString()
				: ISOStringValue;
		const defaultDateOnly = DateOnly.fromISODateString(defaultDate);
		const dateOnlyToday = DateOnly.fromDateAccordingToLocalTime(new Date());

		const dateOnlyFromISOString =
			ISOStringValue !== null ? DateOnly.fromISODateString(ISOStringValue) : null;

		const pastDue = Boolean(
			isDue && dateOnlyFromISOString && DateOnly.compare(dateOnlyFromISOString, dateOnlyToday) < 0,
		);

		const dueToday = Boolean(
			isDue &&
				dateOnlyFromISOString &&
				DateOnly.compare(dateOnlyFromISOString, dateOnlyToday) === 0,
		);

		const staticContent = useMemo(() => {
			const renderDate = () => {
				if (ISOStringValue) {
					if (isVisualRefreshEnabled()) {
						if (expVal('color_due_date_all_views', 'showColoredDueDate', false)) {
							return (
								<ColoredDueDate
									dateString={ISOStringValue}
									pastDue={pastDue}
									dueToday={dueToday}
									clockIconTestId="business-list.ui.list-view.date-cell.clock-icon"
									warningIconTestId="business-list.ui.list-view.date-cell.warning-icon"
									calendarIconTestId="business-list.ui.list-view.date-cell.calendar-icon"
								/>
							);
						}
						return (
							<Flex xcss={[dateTagStyles, pastDue ? overdueStyles : normalDateStyles]}>
								{formatDate(dateOnlyFromISOString)}
							</Flex>
						);
					}
					return (
						<DateLozenge id={!pastDue ? DATE_LOZENGE_ID : ''} pastDue={pastDue}>
							{formatDate(dateOnlyFromISOString)}
						</DateLozenge>
					);
				}
			};

			return (
				ISOStringValue !== null &&
				ISOStringValue !== '' && (
					<Flex xcss={dateLozengeStyles} justifyContent="start" alignItems="center">
						{renderDate()}
					</Flex>
				)
			);
		}, [ISOStringValue, pastDue, dueToday, dateOnlyFromISOString, formatDate]);

		const popupContent = useCallback(
			() => (
				<DropdownWrapper>
					<Calendar
						defaultSelected={[defaultDate]}
						defaultDay={defaultDateOnly.getDay()}
						defaultMonth={defaultDateOnly.getMonth()}
						defaultYear={defaultDateOnly.getYear()}
						defaultPreviouslySelected={EMPTY_ARR}
						defaultDisabled={EMPTY_ARR}
						innerProps={CALENDAR_PROPS}
						onSelect={handleDateSelect}
						locale={standardizedLocale}
						testId={TEST_ID_PREFIX}
						weekStartDay={weekStartDay}
					/>
				</DropdownWrapper>
			),
			[defaultDate, defaultDateOnly, handleDateSelect, standardizedLocale, weekStartDay],
		);

		const popupTrigger = useCallback(
			(triggerProps: TriggerProps) => (
				<div {...triggerProps}>
					{editMode ? (
						<DateForm data-testid="business-list.ui.list-view.date-cell.form">
							<Textfield
								appearance="none"
								value={textInputValue}
								autoFocus
								onChange={handleChange}
								placeholder={localizationProvider.formatDate(new Date(new Date().getTime()))}
							/>
						</DateForm>
					) : (
						staticContent
					)}
					{(ISOStringValue === null || ISOStringValue === '') && !editMode && (
						<EmptyCellContent>
							<CalendarIcon spacing="spacious" label={formatMessage(messages.emptyDate)} />
							<Text>{formatMessage(messages.emptyDate)}</Text>
						</EmptyCellContent>
					)}
				</div>
			),
			[
				editMode,
				textInputValue,
				handleChange,
				localizationProvider,
				staticContent,
				ISOStringValue,
				formatMessage,
			],
		);

		const content = useMemo(
			() =>
				isHovering || editMode ? (
					<Popup
						isOpen={editMode}
						placement="bottom-start"
						offset={POPUP_OFFSET}
						autoFocus={false}
						content={popupContent}
						trigger={popupTrigger}
					/>
				) : (
					staticContent
				),
			[staticContent, editMode, isHovering, popupContent, popupTrigger],
		);

		const cellOverrides = useMemo(
			() => () => ({
				onClick: handleClick,
				onKeyDown: handleKeyDownNew,
			}),
			[handleClick, handleKeyDownNew],
		);
		useImperativeHandle(cellRef, cellOverrides, [cellOverrides]);

		const dateCell = (
			<>
				{content}
				{error && (
					<FixItErrorFlag
						error={error}
						title={messages.errorTitle}
						description={messages.errorMessage}
						issueType={issueType}
						issueTypeId={issueTypeId}
						fieldId={fieldKey}
						fieldName={fieldName}
					/>
				)}
			</>
		);

		return (
			<DateContainerNew data-testid={testId} tabIndex={0}>
				{dateCell}
			</DateContainerNew>
		);
	},
);

export default DateCell;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DateForm = styled.form({
	marginTop: token('space.025'),
	marginRight: token('space.025'),
	marginBottom: token('space.025'),
	marginLeft: token('space.025'),
});

// this is used to position the Popup while its loading the async content
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownWrapper = styled.div({
	minHeight: '300px',
	minWidth: '320px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DateContainerNew = styled.div({
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `calc(100% - ${SHADOW_SIZE * 2}px)`,
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	input: {
		outline: 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'&:not([data-compact])': {
			height: '100%',
			width: '100%',
			paddingTop: token('space.0'),
			paddingRight: token('space.0'),
			paddingBottom: token('space.0'),
			paddingLeft: token('space.100'),
		},
	},
});

const dateLozengeStyles = xcss({
	width: '100%',
	paddingLeft: 'space.075',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DateLozenge = styled.span<{ pastDue: boolean; isRowHovered?: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	background: ({ pastDue }) =>
		pastDue ? token('color.background.danger') : token('color.background.neutral'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: ({ pastDue }) => (pastDue ? token('color.text.danger') : token('color.text.subtle')),
	borderRadius: token('border.radius'),
	paddingTop: token('space.025'),
	paddingRight: token('space.050'),
	paddingBottom: token('space.025'),
	paddingLeft: token('space.050'),
	pointerEvents: 'none',
});

const dateTagStyles = xcss({
	borderStyle: 'solid',
	borderRadius: 'border.radius',
	borderWidth: 'border.width',
	paddingLeft: 'space.075',
	paddingRight: 'space.075',
	paddingTop: 'space.025',
	paddingBottom: 'space.025',
});

const overdueStyles = xcss({
	color: 'color.text.danger',
	borderColor: 'color.border.danger',
});

const normalDateStyles = xcss({
	color: 'color.text',
	borderColor: 'color.border',
});
